import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import { useGlobalStore } from '@/store/app'

/**
 * Authentication route guard that checks if user is authenticated
 * If not authenticated, opens login modal and prevents navigation
 * @param to - Target route
 * @param from - Current route
 * @param next - Navigation function
 */
export const requireAuth = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const appStore = useGlobalStore()

  console.log('requireAuth', appStore.isAuthenticated)
  // If authentication state is unknown (undefined), allow navigation and check in background
  if (appStore.isAuthenticated === undefined) {
    next() // Allow navigation to proceed so GroupContainer can mount

    // Background check for authentication state
    const checkAuthState = () => {
      console.log('checkAuthState', appStore.isAuthenticated)
      if (appStore.isAuthenticated === true) {
        // User is authenticated, do nothing (navigation already allowed)
        return
      } else if (appStore.isAuthenticated === false) {
        // User is not authenticated, show login modal
        appStore.openLoginModal('LOGIN')
        return
      }

      // Still undefined, check again in 100ms
      setTimeout(checkAuthState, 100)
    }

    // Start background checking after a brief delay to allow initialization
    setTimeout(checkAuthState, 200)
    return
  }

  if (appStore.isAuthenticated) {
    // User is authenticated, allow navigation
    next()
  } else {
    // User is not authenticated, open login modal and prevent navigation
    appStore.openLoginModal('LOGIN')
    next(false) // Prevent navigation
  }
}

/**
 * Alternative authentication guard that redirects to a specific route instead of opening modal
 * @param redirectRoute - Route name to redirect to if not authenticated
 */
export const requireAuthWithRedirect = (redirectRoute: string = 'home') => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const appStore = useGlobalStore()

    if (appStore.isAuthenticated) {
      next()
    } else {
      // Store the intended route for after login
      appStore.setRedirectUrl(to.fullPath)
      // Redirect to login route
      next({ name: redirectRoute })
    }
  }
}

/**
 * Guard that requires authentication and opens login modal with custom login page
 * @param loginPage - The login page type ('login', 'signup', etc.)
 */
export const requireAuthWithPage = (loginPage: string = 'LOGIN') => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const appStore = useGlobalStore()

    if (appStore.isAuthenticated) {
      next()
    } else {
      // Open login modal with specific page
      appStore.openLoginModal(loginPage)
      next(false)
    }
  }
}
